import type { Deck } from '@edh/shared';
import { identityGradient } from '@edh/shared';
import { useDeckArt, usePartnerArt } from '../lib/art';

/**
 * A deck's face: its commander art, or its colour identity if there is none.
 *
 * Rounded square rather than a circle, so a deck never reads as a person at a
 * glance — the two sit side by side on every seat card.
 *
 * A pair gets both faces, split down the same diagonal the banners use. It is
 * small, and it is still worth doing: at a table where two people brought
 * partner decks, the split is the thing that tells them apart before any name
 * has been read.
 */
export function DeckThumb({ deck, size = 48 }: { deck: Deck | undefined; size?: number }) {
  const art = useDeckArt(deck);
  const partnerArt = usePartnerArt(deck);
  const paired = Boolean(deck?.partner && partnerArt);

  const face = {
    backgroundSize: 'cover',
    backgroundPosition: 'center 28%',
  } as const;

  return (
    <span
      aria-hidden="true"
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.24),
        flexShrink: 0,
        display: 'inline-block',
        overflow: 'hidden',
        border: '1px solid var(--hairline)',
        // When paired, the two halves cover everything but the diagonal between
        // them, so this shows through as the seam. Gold, matching the banner.
        backgroundColor: paired ? 'var(--gold)' : 'var(--panel)',
        ...(paired
          ? {}
          : {
              ...face,
              backgroundImage: art ? `url(${art})` : identityGradient(deck?.colors ?? [], '145deg'),
              // Unregistered art still gets the colour identity, which is enough
              // to tell two decks apart in a picker.
              opacity: art ? 1 : 0.7,
            }),
      }}
    >
      {paired && (
        <>
          <span
            style={{
              position: 'absolute',
              inset: 0,
              ...face,
              backgroundImage: art
                ? `url(${art})`
                : identityGradient(deck?.colors ?? [], '145deg'),
              /*
               * Slanted hard, because this is 48px square.
               *
               * The banner gets the same angle for free: it is wide and short,
               * so a few percent of horizontal travel is already a long lean.
               * Here the box is as tall as it is wide, and the same percentage
               * came out at about three pixels — a seam that read as vertical
               * and made the pair look like two decks stacked side by side
               * rather than one cut in half.
               */
              clipPath: 'polygon(0 0, 60% 0, 40% 100%, 0 100%)',
            }}
          />
          <span
            style={{
              position: 'absolute',
              inset: 0,
              ...face,
              backgroundImage: `url(${partnerArt})`,
              clipPath: 'polygon(62% 0, 100% 0, 100% 100%, 42% 100%)',
            }}
          />
        </>
      )}
    </span>
  );
}
